import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const LINKS = [
  { to: "/", label: "Beranda" },
  { to: "/tentang", label: "Tentang" },
  { to: "/layanan", label: "Layanan" },
  { to: "/kasus", label: "Direktori Kasus" },
  { to: "/tim", label: "Personalia" },
];

const MEDIA_SUB = [
  { to: "/artikel", label: "Artikel" },
  { to: "/galeri", label: "Galeri" },
  { to: "/berita", label: "Berita Hukum" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mobileMediaOpen, setMobileMediaOpen] = useState(false);
  const location = useLocation();
  const isMediaActive =
    location.pathname.startsWith("/artikel") ||
    location.pathname.startsWith("/galeri") ||
    location.pathname.startsWith("/berita");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "border-b border-white/10 bg-black/60 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-6 lg:h-24 lg:px-10">
        <Link to="/" data-testid="navbar-logo" className="group flex items-center">
          <img
            src="/logo-full.png"
            alt="Logo YLBH Sumatera Timur — Berani, Peduli, Berintegritas"
            className="h-14 w-auto transition-transform duration-300 group-hover:scale-105 lg:h-[4.75rem]"
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigasi utama">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-[#C9C9C9] hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={`absolute -bottom-2 left-0 h-px bg-gold transition-all duration-500 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setMediaOpen(true)}
            onMouseLeave={() => setMediaOpen(false)}
          >
            <button
              data-testid="nav-link-media"
              onClick={() => setMediaOpen(true)}
              onFocus={() => setMediaOpen(true)}
              aria-expanded={mediaOpen}
              aria-haspopup="true"
              className={`relative flex items-center gap-1.5 text-[13px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                isMediaActive ? "text-gold" : "text-[#C9C9C9] hover:text-white"
              }`}
            >
              Media
              <ChevronDown
                size={13}
                className={`transition-transform duration-300 ${mediaOpen ? "rotate-180" : ""}`}
              />
              <span
                className={`absolute -bottom-2 left-0 h-px bg-gold transition-all duration-500 ${
                  isMediaActive ? "w-full" : "w-0"
                }`}
              />
            </button>
            <AnimatePresence>
              {mediaOpen && (
                <motion.div
                  data-testid="media-dropdown"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 top-full z-50 mt-4 w-48 -translate-x-1/2 border border-white/10 bg-[#0A0A0A]/95 py-2 backdrop-blur-xl"
                >
                  {MEDIA_SUB.map((sub) => (
                    <NavLink
                      key={sub.to}
                      to={sub.to}
                      data-testid={`media-link-${sub.label.toLowerCase()}`}
                      onClick={() => setMediaOpen(false)}
                      className={({ isActive }) =>
                        `block px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.2em] transition-colors ${
                          isActive ? "text-gold" : "text-[#C9C9C9] hover:text-white"
                        }`
                      }
                    >
                      {sub.label}
                    </NavLink>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink
            to="/kontak"
            data-testid="nav-link-kontak"
            className={({ isActive }) =>
              `relative text-[13px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                isActive ? "text-gold" : "text-[#C9C9C9] hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                Kontak
                <span
                  className={`absolute -bottom-2 left-0 h-px bg-gold transition-all duration-500 ${
                    isActive ? "w-full" : "w-0"
                  }`}
                />
              </>
            )}
          </NavLink>
          <Link
            to="/kontak"
            data-testid="nav-konsultasi-button"
            className="border border-gold/50 px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
          >
            Konsultasi
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Tutup menu" : "Buka menu"}
          onClick={() => setOpen((v) => !v)}
          className="text-white lg:hidden"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/80 backdrop-blur-xl lg:hidden"
            aria-label="Navigasi seluler"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-2xl ${
                        isActive ? "text-gold" : "text-[#C9C9C9]"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * LINKS.length }}
              >
                <button
                  data-testid="mobile-nav-media-toggle"
                  onClick={() => setMobileMediaOpen((v) => !v)}
                  aria-expanded={mobileMediaOpen}
                  className={`flex w-full items-center justify-between py-3 font-serif text-2xl ${
                    isMediaActive ? "text-gold" : "text-[#C9C9C9]"
                  }`}
                >
                  Media
                  <ChevronDown
                    size={20}
                    className={`transition-transform duration-300 ${mobileMediaOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {mobileMediaOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      {MEDIA_SUB.map((sub) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          data-testid={`mobile-media-link-${sub.label.toLowerCase()}`}
                          className={({ isActive }) =>
                            `block border-l border-gold/30 py-2.5 pl-5 font-serif text-xl ${
                              isActive ? "text-gold" : "text-[#A3A3A3]"
                            }`
                          }
                        >
                          {sub.label}
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * (LINKS.length + 1) }}
              >
                <NavLink
                  to="/kontak"
                  data-testid="mobile-nav-link-kontak"
                  className={({ isActive }) =>
                    `block py-3 font-serif text-2xl ${
                      isActive ? "text-gold" : "text-[#C9C9C9]"
                    }`
                  }
                >
                  Kontak
                </NavLink>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
